import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import { getAllRoutes, getAllRoutesByCompany } from "../../services";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

interface Gb1SaleReportFilterProps {
  date: Dayjs;
  setDate: Dispatch<SetStateAction<Dayjs>>;
  selectedRoute: string;
  setSelectedRoute: Dispatch<SetStateAction<string>>;
  selectedBranch: string;
  setSelectedBranch: Dispatch<SetStateAction<string>>;
  branches: Branch[];
}

export default function Gb1SaleReportFilter({
  date,
  setDate,
  selectedRoute,
  setSelectedRoute,
  selectedBranch,
  setSelectedBranch,
  branches,
}: Readonly<Gb1SaleReportFilterProps>) {
  const { isHeadOfficeAccounting } = useUserRole();
  const [saleRoutes, setSaleRoutes] = useState<SaleRoute[]>([]);

  const getRoutes = useCallback(async () => {
    if (isHeadOfficeAccounting) {
      if (selectedBranch === "all") {
        setSaleRoutes([]);
        return;
      }
      const {
        data: { data },
      } = await getAllRoutes(selectedBranch);
      setSaleRoutes(Array.isArray(data) ? data : data.rows ?? []);
    } else {
      const {
        data: { data },
      } = await getAllRoutesByCompany();
      setSaleRoutes(data);
    }
  }, [isHeadOfficeAccounting, selectedBranch]);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  const isRouteDisabled = isHeadOfficeAccounting && selectedBranch === "all";

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
        },
        alignItems: "flex-start",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <DatePicker
          value={date}
          format="DD/MM/YYYY"
          label="วันที่"
          slotProps={{
            textField: { size: "small", fullWidth: true },
          }}
          onChange={(value) => value && setDate(value)}
        />
      </LocalizationProvider>

      {isHeadOfficeAccounting && (
        <TextField
          select
          fullWidth
          size="small"
          label="สาขา"
          value={selectedBranch}
          onChange={(e) => {
            setSelectedBranch(e.target.value);
            setSelectedRoute("all");
          }}
        >
          <MenuItem value="all">เลือกสาขา</MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.uuid} value={branch.company_id}>
              {branch.branch_code} - {branch.branch_name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        fullWidth
        size="small"
        label="สายส่ง"
        disabled={isRouteDisabled}
        value={selectedRoute}
        onChange={(e) => setSelectedRoute(e.target.value)}
        helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
      >
        <MenuItem value="all">ทั้งหมด</MenuItem>
        {saleRoutes.map((route) => (
          <MenuItem key={route.uuid} value={route.uuid}>
            {route.route_code} - {route.route_name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
